import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import { ToWords } from 'to-words';
import { getReservationDetailsObject } from 'utils/utils';
import { getBillingInvoiceDetails } from 'redux/actions/BillingAction';
import CommonNoDataFound from 'components/CommonNoDataFound/CommonNoDataFound';
import Loader from 'components/loader/Loader';
import { tripPerformaInvoiceData } from 'pages/operation/billing/billingFlow/billingInvoice/BillingInvoiceData';
import { tripDutyTypeEnum } from 'utils/enumObj';
import { companyDetails } from 'utils/companyDetails';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import DescriptionIcon from '@mui/icons-material/Description';

// Helper function to get toll and parking charges
const getTollAndParkingCharges = (tripDetails) => {
  const tollCharges =
    tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(
      (item) => item.chargeType === 'tollCharges',
    )?.addedCharge || 0;
  const parkingCharges =
    tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(
      (item) => item.chargeType === 'parkingCharge',
    )?.addedCharge || 0;

  if (tollCharges || parkingCharges) {
    return parseFloat(tollCharges) + parseFloat(parkingCharges);
  }

  return parseFloat(tripDetails?.tollAndParkingCharges || 0);
};

// Helper function to calculate Sub Total A (base fare + extra charges + surcharges + platform charge)
// Overage charges come from the pricing-engine breakdown (chargeType extraKm /
// extraHrs); the legacy top-level fields only exist on the old pricing path.
const calculateSubTotalA = (tripDetails) => {
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
  const breakdown = tripPricing?.breakdown || [];
  const baseFare = tripDetails?.Payments?.[0]?.tripPricingDetails?.baseFareDisplay || 0;
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  const extraKmFromBreakdown = breakdown.find(
    (b) => b?.chargeType?.toLowerCase() === 'extrakm',
  )?.addedCharge;
  const extraKmCharges =
    extraKmFromBreakdown !== undefined && extraKmFromBreakdown !== null
      ? parseFloat(extraKmFromBreakdown) || 0
      : tripDetails?.type === tripDutyTypeEnum?.rental
        ? parseFloat(tripDetails?.extraKmCharges || 0)
        : parseFloat(tripDetails?.extraPrice || 0);
  const extraHrFromBreakdown = breakdown.find(
    (b) => b?.chargeType?.toLowerCase() === 'extrahrs',
  )?.addedCharge;
  const extraHrCharges =
    extraHrFromBreakdown !== undefined && extraHrFromBreakdown !== null
      ? parseFloat(extraHrFromBreakdown) || 0
      : parseFloat(tripDetails?.extraHrCharges || 0);

  return baseFare + baseRate + surCharge + platformCharge + extraKmCharges + extraHrCharges;
};

// Helper function to calculate Sub Total B (all additional charges)
const calculateSubTotalB = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];

  const tollAndParking = getTollAndParkingCharges(tripDetails);
  const driverAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'driverAllowance')?.addedCharge || 0,
  );
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'nightAllowance')?.addedCharge || 0,
  );
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const placardCharge = parseFloat(
    breakdown.find((b) => b.chargeType === 'placard')?.addedCharge || 0,
  );
  const garageCharge = parseFloat(
    breakdown.find((b) => b.chargeType === 'garageCharge')?.addedCharge || 0,
  );
  const additionalCharges = parseFloat(
    breakdown.find((b) => b.chargeType === 'additionalCharges')?.addedCharge || 0,
  );
  const couponDiscount = Math.abs(
    parseFloat(breakdown.find((b) => b.chargeType === 'discountAmount')?.addedCharge || 0),
  );

  return (
    tollAndParking +
    driverAllowance +
    handlingCharges +
    nightAllowance +
    fuelSurcharge +
    interstateTax +
    placardCharge +
    garageCharge +
    additionalCharges -
    couponDiscount
  );
};

const BillingPerformaInvoice = () => {
  const dispatch = useDispatch();
  const toWords = new ToWords();
  const [searchParams] = useSearchParams();

  const params = Object.fromEntries([...searchParams]);

  const queryParams = {
    magicToken: params?.magicToken || '',
  };

  useEffect(() => {
    dispatch(getBillingInvoiceDetails(queryParams?.magicToken, 'public'));
  }, []);

  const tripDetails = useSelector(
    (state) => state.getBillingInvoiceDetails?.bilingInvoiceDetails?.data,
  );
  const showVendorName = tripDetails?.tripCompany?.additionalInfo?.showVendorName;
  const vendorDisplayName = tripDetails?.subVendorCompanyName || tripDetails?.vendorName || 'NA';
  const tripDetailsLoader = useSelector((state) => state.getBillingInvoiceDetails?.loading);
  const tripTypeDetails = getReservationDetailsObject(tripDetails);
  const showStart = tripDetails?.additionalInfo?.startOtpRequired;
  const showEnd = tripDetails?.additionalInfo?.endOtpRequired;
  const bothShown = showStart && showEnd;
  const subTotalA = calculateSubTotalA(tripDetails);
  const subTotalB = calculateSubTotalB(tripDetails);
  const totalAB = Number(subTotalA) + Number(subTotalB);

  const totalGstAmount =
    totalAB *
    0.01 *
    (Number(tripDetails?.Payments?.[0]?.igst) +
      Number(tripDetails?.Payments?.[0]?.cgst) +
      Number(tripDetails?.Payments?.[0]?.sgst));
  const grossTotal = (totalAB + totalGstAmount).toFixed(2);
  const tripPerformaInvoiceDetails = tripPerformaInvoiceData(
    tripDetails,
    tripTypeDetails,
    subTotalA,
    subTotalB,
    totalAB,
    totalGstAmount,
    grossTotal,
  );
  const tollImages =
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === 'Toll');
  const parkingImages =
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === 'Parking');

  const interStateImages =
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === 'Interstate-Tax');

  const commonGridRender = (arrayData, gridSize) => {
    return arrayData?.map((details, index) => (
      <Grid
        container
        item
        xs={gridSize?.xs}
        sm={gridSize?.sm}
        md={gridSize?.md}
        key={index}
        className={`pb5 ${details?.title !== 'Company Address' ? 'maxHeight30' : ''}`}
      >
        <Grid
          item
          xs={6}
          lg={6}
          xl={6}
          className="textleft"
          sx={{ padding: { xs: '0px 0px 0px 10px', md: '0px 0px 0px 24px' } }}
          container
        >
          <Typography
            variant="body7"
            className={`"colorBlack" ${details?.bold ? 'fw600' : 'op80'}`}
            component={'p'}
          >
            {details?.title}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          lg={6}
          xl={6}
          className="colorBlack textLeft"
          container
          sx={{ padding: { xs: '0px 0px 0px 4px', md: '0px 0px 0px 24px' } }}
        >
          <Typography
            variant="body7"
            className={` ${details?.bold ? 'fw600' : 'fw400'}`}
            component={'p'}
          >
            {details?.value}
          </Typography>
        </Grid>
      </Grid>
    ));
  };

  return (
    <Grid container className="br20" justifyContent={'center'}>
      {tripDetailsLoader ? (
        <Loader />
      ) : (
        <Grid item md={11}>
          <Grid container justifyContent={'space-between'} alignItems={'center'}></Grid>

          <Grid className="invoiceBorder br20 mb250">
            <Grid container className="solidBorderBottomBlack pt10" item xs={12}>
              <Grid
                item
                xs={12}
                sx={{
                  padding: { xs: '0px 0px 0px 10px', md: '0px 0px 0px 24px' },
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    gap: 12,
                  }}
                >
                  {/* Left: Logo + Company Info */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    <img
                      src={tripDetails?.issuerLogo || companyDetails?.companyLogo}
                      alt="Logo"
                      width="240"
                      height="240"
                      style={{
                        height: 'clamp(50px, 8vw, 80px)',
                        width: 'clamp(50px, 8vw, 80px)',
                        flexShrink: 0,
                        imageRendering: 'auto',
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <Typography
                        className="fw500 colorPrimary"
                        style={{
                          marginBottom: 4,
                          fontSize: 'clamp(13px, 1.8vw, 16px)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tripDetails?.vendorName || 'NA'}
                      </Typography>
                      {tripDetails?.vendorAddress && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 4,
                            marginBottom: 3,
                          }}
                        >
                          <LocationOnIcon
                            sx={{
                              color: 'var(--brand-primary)',
                              fontSize: 'clamp(10px, 1.2vw, 14px)',
                              marginTop: '2px',
                              flexShrink: 0,
                            }}
                          />
                          <Typography style={{ fontSize: 'clamp(10px, 1.2vw, 13px)' }}>
                            {tripDetails?.vendorAddress}
                          </Typography>
                        </div>
                      )}
                      {tripDetails?.vendorEmail && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            marginBottom: 3,
                          }}
                        >
                          <EmailIcon
                            sx={{
                              color: 'var(--brand-primary)',
                              fontSize: 'clamp(10px, 1.2vw, 14px)',
                              flexShrink: 0,
                            }}
                          />
                          <Typography style={{ fontSize: 'clamp(10px, 1.2vw, 13px)' }}>
                            {tripDetails?.vendorEmail}
                          </Typography>
                        </div>
                      )}
                      {tripDetails?.vendorMobile && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <PhoneIcon
                            sx={{
                              color: 'var(--brand-primary)',
                              fontSize: 'clamp(10px, 1.2vw, 14px)',
                              flexShrink: 0,
                            }}
                          />
                          <Typography style={{ fontSize: 'clamp(10px, 1.2vw, 13px)' }}>
                            {tripDetails?.vendorMobile}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Divider + GST */}
                  {tripDetails?.vendorGstNo && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        flexShrink: 0,
                        marginLeft: -8,
                        marginRight: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 1,
                          height: 50,
                          backgroundColor: '#e0e0e0',
                        }}
                      />
                      <div
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 6,
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          backgroundColor: '#f5f5f5',
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: 'var(--brand-primary)',
                            borderRadius: '50%',
                            padding: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <DescriptionIcon sx={{ color: '#ffffff', fontSize: 16 }} />
                        </div>
                        <div>
                          <Typography
                            style={{
                              fontSize: 10,
                              color: '#000',
                              lineHeight: 1.2,
                            }}
                          >
                            GST No.
                          </Typography>
                          <Typography
                            className="fw500"
                            style={{
                              fontSize: 12,
                              whiteSpace: 'nowrap',
                              lineHeight: 1.3,
                            }}
                          >
                            {tripDetails?.vendorGstNo}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Grid>

              <Grid container item xs={6}>
                {tripPerformaInvoiceDetails?.companyGstDetails1?.map((details, index) => (
                  <Grid container item xs={12} key={index} className="pb5 maxHeight20">
                    <Grid
                      item
                      xs={6}
                      className="textleft"
                      sx={{
                        padding: {
                          xs: '0px 0px 0px 10px',
                          md: '0px 0px 0px 24px',
                        },
                      }}
                    >
                      <Typography variant="body7" className="colorBlack op80" component={'p'}>
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xl={8} className="colorBlack textLeft">
                      <Typography variant="body7" component={'p'}>
                        {details?.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid
                container
                item
                xs={6}
                alignItems={'flex-start'}
                className="height100 flexColumn"
              >
                {tripPerformaInvoiceDetails?.companyDetails2?.map((details, index) => (
                  <Grid container item xs={12} key={index} className="pb5 maxHeight20">
                    <Grid
                      item
                      xs={6}
                      className="textleft"
                      sx={{
                        padding: {
                          xs: '0px 0px 0px 10px',
                          md: '0px 0px 0px 24px',
                        },
                      }}
                    >
                      <Typography variant="body7" className="colorBlack op80" component={'p'}>
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} className="colorBlack textLeft">
                      <Typography variant="body7" component={'p'}>
                        {details?.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pt10 pb10" item xs={12}>
              <Grid container item xs={6}>
                {tripPerformaInvoiceDetails?.detailsArray1?.map((details, index) => (
                  <Grid
                    container
                    item
                    xs={12}
                    key={index}
                    className={`pb5 ${details?.title !== 'Company Address' ? 'maxHeight20' : ''}`}
                  >
                    <Grid
                      item
                      xs={6}
                      lg={5}
                      xl={4}
                      className="textleft"
                      sx={{
                        padding: {
                          xs: '0px 0px 0px 10px',
                          md: '0px 0px 0px 24px',
                        },
                      }}
                    >
                      <Typography variant="body7" className="colorBlack op80" component={'p'}>
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} lg={7} xl={8} className="colorBlack textLeft">
                      <Typography variant="body7" component={'p'}>
                        {details?.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid
                container
                item
                xs={6}
                alignItems={'flex-start'}
                className="height100 flexColumn"
              >
                {tripPerformaInvoiceDetails?.detailsArray2?.map((details, index) => (
                  <Grid container item xs={12} key={index} className="pb5 maxHeight20">
                    <Grid
                      item
                      xs={6}
                      lg={5}
                      xl={4}
                      className="textleft"
                      sx={{
                        padding: {
                          xs: '0px 0px 0px 10px',
                          md: '0px 0px 0px 24px',
                        },
                      }}
                    >
                      <Typography variant="body7" className="colorBlack op80" component={'p'}>
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} lg={7} xl={8} className="colorBlack textLeft">
                      <Typography variant="body7" component={'p'}>
                        {details?.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pt10" item xs={12}>
              <Grid container item xs={12} md={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray3, {
                  xs: 4,
                })}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pt10" item xs={12}>
              <Grid container item xs={12} md={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray4, {
                  xs: 4,
                })}
              </Grid>
              <Grid container item xs={12} md={12} className="pt10 solidBorderTopBlack">
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray4Summary, {
                  xs: 4,
                })}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pt10" item xs={12}>
              <Grid container item xs={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray5, {
                  xs: 4,
                })}
              </Grid>
              <Grid container item xs={12} md={12} className="pt10 solidBorderTopBlack">
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray5Summary, {
                  xs: 4,
                })}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pb10 m40" item xs={12}>
              <Grid item xs={8} className="pt10">
                {showVendorName && (
                  <Typography
                    variant="body7"
                    className="fw500 pb20 colorSolidBlack"
                    sx={{
                      padding: {
                        xs: '0px 0px 10px 10px',
                        md: '0px 0px 10px 24px',
                      },
                    }}
                    component={'p'}
                  >
                    All Cheques/Draft to be drawn in favor of {vendorDisplayName}
                  </Typography>
                )}
                {tripPerformaInvoiceDetails?.detailsArray6 &&
                  tripPerformaInvoiceDetails?.detailsArray6?.map((routingData, index) => (
                    <Grid
                      container
                      className="pb10 maxHeight30"
                      sx={{
                        padding: {
                          xs: '0px 0px 10px 10px',
                          md: '0px 0px 10px 24px',
                        },
                      }}
                    >
                      <Typography variant="body7" className="colorSolidBlack" component={'p'}>
                        {index + 1}.&nbsp;{routingData}
                      </Typography>
                    </Grid>
                  ))}
                <Grid
                  item
                  pt={3}
                  sx={{
                    padding: { xs: '0px 0px 0px 10px', md: '0px 0px 0px 24px' },
                  }}
                >
                  <Typography variant="body7" className="colorSolidBlack fw600" component={'p'}>
                    Net Payable In Words:{' '}
                    {!isNaN(tripDetails?.finalPrice)
                      ? toWords
                          .convert(Number(tripDetails?.finalPrice), {
                            currency: true,
                          })
                          .toUpperCase()
                      : 'Zero'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={4}>
                {(tripDetails?.Payments?.[0]?.igst !== 0
                  ? tripPerformaInvoiceDetails?.detailsArray7
                  : tripPerformaInvoiceDetails?.detailsArray8
                )?.map((details, index) => (
                  <Grid container item xs={12} key={index} className="pt10 maxHeight30">
                    <Grid
                      item
                      xs={7}
                      className="textleft"
                      sx={{
                        padding: {
                          xs: '0px 0px 0px 10px',
                          md: '0px 0px 0px 24px',
                        },
                      }}
                    >
                      <Typography
                        variant="body7"
                        className={`"colorBlack" ${details?.bold ? 'fw600' : 'op80'}`}
                        component={'p'}
                      >
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={5} className={`"colorBlack textLeft"`}>
                      <Typography
                        variant="body7"
                        className={` ${details?.bold ? 'fw600' : 'fw400'}`}
                        component={'p'}
                      >
                        {details?.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid container className="mt20 m40">
              <Grid item xs={12} sm={5.5} className="height200 bgGrey justifyCenter">
                {tripDetails?.tripRouteImage?.url ? (
                  <img
                    src={tripDetails?.tripRouteImage?.url}
                    alt="GoogleImage"
                    className="width100 height100"
                  />
                ) : (
                  <CommonNoDataFound />
                )}
              </Grid>
              <Grid item xs={12} sm={5.5} className="plr20">
                <Box className="mb20">
                  <Typography className="fw600 maxHeight30" variant="body5" component={'p'}>
                    Guest Start Location
                  </Typography>
                  <Typography className="" variant="body5" component={'p'}>
                    {tripDetails?.tripStartLocation || tripDetails?.sourceAddress || ''}
                  </Typography>
                </Box>
                <Box className="mb10">
                  <Typography className="fw600" variant="body5" component={'p'}>
                    Guest Drop Location
                  </Typography>
                  <Typography className="" variant="body5" component={'p'}>
                    {tripDetails?.tripEndLocation || tripDetails?.destinationAddress || ''}
                  </Typography>
                </Box>
                <Typography className="fw600 pb10" variant="body3" component={'p'}>
                  Distance Covered:&nbsp;&nbsp;&nbsp;
                  {tripDetails?.actualDistance || 0} km
                </Typography>
                {(showStart || showEnd) && (
                  <Grid container>
                    {showStart && (
                      <Grid item xs={12} sm={bothShown ? 6 : 12} className="justifyCenter">
                        <Typography className="textCenter colorPrimary fw600">
                          Start Trip OTP Verified
                          <br />
                          {tripDetails?.TripOtp?.startOtp || ''}
                        </Typography>
                      </Grid>
                    )}
                    {showEnd && (
                      <Grid item xs={12} sm={bothShown ? 6 : 12} className="justifyCenter">
                        <Typography className="textCenter colorPrimary fw600">
                          End Trip OTP Verified
                          <br />
                          {tripDetails?.TripOtp?.endOtp || ''}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}

                {tripDetails?.guestSignature?.url && (
                  <Grid container item xs={12} className="mt20">
                    <Grid item xs={12} className="justifyCenter">
                      <Typography className="fw600 maxHeight30" variant="body5" component={'p'}>
                        Guest Signature
                      </Typography>
                    </Grid>
                    <Grid item xs={12} className="justifyCenter">
                      <img
                        src={tripDetails?.guestSignature?.url}
                        alt="Guest Signature"
                        style={{ maxHeight: '80px', objectFit: 'contain' }}
                      />
                    </Grid>
                    {tripDetails?.guestSignatureCapturedAt && (
                      <Grid item xs={12} className="justifyCenter">
                        <Typography variant="body7" className="colorGrey">
                          Signed on:{' '}
                          {new Date(tripDetails?.guestSignatureCapturedAt).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}

                {tripDetails?.additionalInfo?.sealSignature?.enabled && (
                  <Grid container item xs={12} className="mt20">
                    <Grid item xs={12} className="justifyCenter">
                      <Typography className="fw600 maxHeight30" variant="body5" component={'p'}>
                        Seal & Signature
                      </Typography>
                    </Grid>
                    <Grid item xs={12} className="justifyCenter" sx={{ display: 'flex', gap: 2 }}>
                      {tripDetails?.additionalInfo?.sealSignature?.sealUrl && (
                        <img
                          src={tripDetails?.additionalInfo?.sealSignature?.sealUrl}
                          alt="Seal"
                          style={{ maxHeight: '80px', objectFit: 'contain' }}
                        />
                      )}
                      {tripDetails?.additionalInfo?.sealSignature?.signatureUrl && (
                        <img
                          src={tripDetails?.additionalInfo?.sealSignature?.signatureUrl}
                          alt="Signature"
                          style={{ maxHeight: '80px', objectFit: 'contain' }}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          {/* TODO: required an data from backend */}
          {tripDetails?.dutyTypeName === 'Rental' && tripDetails?.RoutingSummaries.length > 0 && (
            <Grid className="mb40">
              <Typography variant="body3" className="fw600 pb30">
                Routing Summary Form Pick-Up To Drop
              </Typography>
              <Grid container className="solidBorderBottomBlack pb20 mt20">
                <Grid xs={12} sm={1} className="justifyCenter">
                  <Typography variant="body3" className="textCenter">
                    Sr. No.
                  </Typography>
                </Grid>
                <Grid xs={12} sm={11}>
                  <Typography variant="body3">Location</Typography>
                </Grid>
              </Grid>
              {tripDetails?.RoutingSummaries &&
                tripDetails?.RoutingSummaries?.map((routingData, index) => (
                  <Grid container className="ptb10 dashedBorderBottom">
                    <Grid xs={12} sm={1} className="justifyCenter">
                      <Typography variant="body5" className="colorGrey textCenter">
                        {index + 1}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={11}>
                      <Typography variant="body5" className="colorGrey">
                        {routingData?.address}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
            </Grid>
          )}

          <Grid container className="mb30" gap={3} flexWrap={'nowrap'}>
            {tollImages?.[0]?.TollAndParkingReceipts &&
              tollImages?.[0]?.TollAndParkingReceipts?.map((tollImages) => (
                <Grid items xs={12} sm={6} md={3}>
                  <img
                    src={tollImages?.url}
                    alt="Toll and parking"
                    className="width100 height200 br5"
                  />
                </Grid>
              ))}
          </Grid>

          <Grid container className="mb40" gap={3} flexWrap={'nowrap'}>
            {parkingImages?.[0]?.TollAndParkingReceipts &&
              parkingImages?.[0]?.TollAndParkingReceipts?.map((parkingImages) => (
                <Grid items xs={12} sm={6} md={3}>
                  <img
                    src={parkingImages?.url}
                    alt="Toll and parking"
                    className="width100 height200 br5"
                  />
                </Grid>
              ))}
          </Grid>
          <Grid container className="mb30" gap={3} flexWrap={'nowrap'}>
            {interStateImages?.[0]?.TollAndParkingReceipts &&
              interStateImages?.[0]?.TollAndParkingReceipts?.map((tollImages) => (
                <Grid items xs={12} sm={6} md={3}>
                  <img
                    src={tollImages?.url}
                    alt="InterState-Rate"
                    className="width100 height200 br5"
                  />
                </Grid>
              ))}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default BillingPerformaInvoice;
